import React, { useEffect, useState } from 'react';

import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import styled from 'styled-components';
import { FlexContainer, GutterCSS, StyledIconButton, StyledTooltip } from 'styles/common';

import {
  AngryColoredEmojiIcon,
  HappyColoredEmojiIcon,
  SadColoredEmojiIcon,
  SmartColoredEmojiIcon,
  ThickOutlineSmileIcon,
  VeryHappyColoredEmojiIcon,
} from 'constants/icons';
import { sendClickedOnMoodQuestion, sendRespondedToMoodQuestion } from 'constants/mixpanelHelper';

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props?.padding || '1rem'};
  &:hover {
    background: aliceblue;
    cursor: pointer;
  }
`;

const Wrapper = styled.div`
  ${GutterCSS}
`;

const CircularIcon = styled(StyledIconButton)`
  border-radius: 50%;
  padding: ${(props) => props?.padding || '1rem'};
  background: ${(props) => props.theme.palette.background.lightGrey3};
`;

const fiveOptionsEmojis = [
  {
    component: AngryColoredEmojiIcon,
    label: 'Extremely sad',
  },
  {
    component: SadColoredEmojiIcon,
    label: 'Sad',
  },
  {
    component: SmartColoredEmojiIcon,
    label: 'Average',
  },
  {
    component: HappyColoredEmojiIcon,
    label: 'Happy',
  },
  {
    component: VeryHappyColoredEmojiIcon,
    label: 'Extremely happy',
  },
];

const fourOptionsEmojis = [
  {
    component: AngryColoredEmojiIcon,
    label: 'Extremely sad',
  },
  {
    component: SadColoredEmojiIcon,
    label: 'Sad',
  },
  {
    component: HappyColoredEmojiIcon,
    label: 'Happy',
  },
  {
    component: VeryHappyColoredEmojiIcon,
    label: 'Extremely happy',
  },
];

const threeOptionsEmojis = [
  {
    component: AngryColoredEmojiIcon,
    label: 'Extremely sad',
  },
  {
    component: SmartColoredEmojiIcon,
    label: 'Average',
  },
  {
    component: VeryHappyColoredEmojiIcon,
    label: 'Extremely happy',
  },
];

export default function EmojiPopupSelect({
  onChange,
  prevVal = null,
  editable = true,
  tooltipText = '',
  options,
  // integer to reset the state in case of dummy usage
  resetVar = 0,
  ...props
}) {
  let prevSelectedEmojiIndex = null;
  options.map(({ score }, idx) => {
    if (score === prevVal) {
      prevSelectedEmojiIndex = idx;
    }
  });
  const [selectedEmojiIndex, setSelectedEmojiIndex] = useState(prevSelectedEmojiIndex);
  // reset invalid selected emoji state on options change
  // for eg, in 5 emoji view 4th was selected, but we could also change the component to render
  // 3 emoji component through props change, in that case the state becomes invalid, as there is no 4th emoji
  if (selectedEmojiIndex > options.length - 1) {
    setSelectedEmojiIndex(prevSelectedEmojiIndex);
  }
  useEffect(() => {
    if (resetVar !== 0) setSelectedEmojiIndex(null);
  }, [resetVar]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const noOfOptions = options.length || 5;

  const emojis = noOfOptions === 3 ? threeOptionsEmojis : noOfOptions === 4 ? fourOptionsEmojis : fiveOptionsEmojis;

  const handleClick = (event) => {
    if (!editable) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const selectedEmoji = (score, idx) => {
    onChange && onChange(score);
    setSelectedEmojiIndex(idx);
    handleClose();
    sendRespondedToMoodQuestion({
      eventProperties: {
        responseGiven: score,
      },
    });
  };

  useEffect(() => {
    if (anchorEl) {
      sendClickedOnMoodQuestion();
    }
  }, [anchorEl]);

  return (
    <Wrapper {...props}>
      <StyledTooltip title={tooltipText}>
        <div onClick={handleClick} style={{ paddingBottom: '16px', display: 'inline' }}>
          <CircularIcon padding="0.5rem" cursor={!editable ? 'default' : 'pointer'}>
            {Number.isNaN(parseInt(selectedEmojiIndex)) ? (
              <ThickOutlineSmileIcon size="fixedSmallest" padding="none" color="#999" />
            ) : (
              <DisplayEmoji noOfEmojis={noOfOptions} selectedEmojiIndex={selectedEmojiIndex} />
            )}
          </CircularIcon>
        </div>
      </StyledTooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <FlexContainer>
          {options.map(({ id, score }, idx) => {
            const EmojiComp = emojis[idx].component;
            const emojiLabel = emojis[idx].label;

            return (
              <Tooltip title={emojiLabel}>
                <ButtonWrapper key={id} onClick={() => selectedEmoji(score, idx)}>
                  <EmojiComp size="fixedSmallest" />
                </ButtonWrapper>
              </Tooltip>
            );
          })}
        </FlexContainer>
      </Popover>
    </Wrapper>
  );
}

export const DisplayEmoji = ({ selectedEmojiIndex, noOfEmojis, rightMargin = '0', ...props }) => {
  let emoji = null;
  let EmojiComp = null;

  if (noOfEmojis === 3) {
    emoji = threeOptionsEmojis[selectedEmojiIndex];
    EmojiComp = emoji.component;
  } else if (noOfEmojis === 4) {
    emoji = fourOptionsEmojis[selectedEmojiIndex];
    EmojiComp = emoji.component;
  } else {
    emoji = fiveOptionsEmojis[selectedEmojiIndex];
    EmojiComp = emoji.component;
  }
  return (
    <Tooltip title={emoji.label}>
      <div
        style={{
          height: '24px',
          marginRight: rightMargin,
        }}
      >
        <EmojiComp size="fixedSmallest" {...props} />
      </div>
    </Tooltip>
  );
};

export const DisplayEmojiFivePoint = ({ selectedEmojiId, rightMargin = '0', ...props }) => {
  const emoji = fiveOptionsEmojis[selectedEmojiId - 1];
  const EmojiComp = emoji.component;

  return (
    <Tooltip title={emoji.label}>
      <div
        style={{
          marginRight: rightMargin,
        }}
      >
        <EmojiComp size="fixedSmallest" {...props} />
      </div>
    </Tooltip>
  );
};
